import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';
import { Settings, Play, Pause, RotateCcw, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { GameSettings, DEFAULT_SETTINGS, Card as GameCard, SavedGame } from '@/types/game';
import { STORAGE_KEYS } from '@/lib/constants';
import { useGameState } from '@/hooks/useGameState';
import { useGameTimer } from '@/hooks/useGameTimer';
import { useWakeLock } from '@/hooks/useWakeLock';
import { initAudioContext } from '@/lib/notifications';
import CardDialog from '@/components/CardDialog';
import ScoreCard from '@/components/ScoreCard';
import ConfirmDialog from '@/components/ConfirmDialog';

function GameView() {
  const [location, setLocation] = useLocation();
  const [settings, setSettings] = useState<GameSettings>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.SETTINGS);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure backward compatibility - add missing fields
        return {
          ...DEFAULT_SETTINGS,
          ...parsed,
          notificationType: parsed.notificationType || 'siren',
          halfTimeBreakDuration: parsed.halfTimeBreakDuration || 5
        };
      }
      return DEFAULT_SETTINGS;
    } catch (err) {
      console.error('Failed to load settings:', err);
      return DEFAULT_SETTINGS;
    }
  });

  const [hasHistory, setHasHistory] = useState(false);

  // Check if there are saved games
  useEffect(() => {
    try {
      const historyJson = localStorage.getItem(STORAGE_KEYS.GAME_HISTORY);
      const history: SavedGame[] = historyJson ? JSON.parse(historyJson) : [];
      setHasHistory(history.length > 0);
    } catch (err) {
      console.error('Failed to check game history:', err);
      setHasHistory(false);
    }
  }, [location]);

  // Reload settings when navigating back to this page
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.SETTINGS);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure backward compatibility - add missing fields
        setSettings({
          ...DEFAULT_SETTINGS,
          ...parsed,
          notificationType: parsed.notificationType || 'siren',
          halfTimeBreakDuration: parsed.halfTimeBreakDuration || 5
        });
      }
    } catch (err) {
      console.error('Failed to reload settings:', err);
    }
  }, [location]);

  const [gameState, setGameState] = useGameState(settings);
  const [showCardDialog, setShowCardDialog] = useState<'home' | 'away' | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [cardToRemove, setCardToRemove] = useState<string | null>(null);

  // Update timers when settings change (only if at initial state)
  useEffect(() => {
    setGameState(prev => {
      // Don't update if timer is running
      if (prev.isRunning) {
        return prev;
      }

      const updates: Partial<GameState> = {};
      
      // Calculate expected duration for current half
      const expectedDuration = prev.currentHalf <= settings.numberOfHalves 
        ? settings.halfDuration * 60 
        : settings.overtimeDuration * 60;
      
      // Update game timer if it differs from expected duration
      if (prev.timeRemaining !== expectedDuration && !prev.isHalfTimeBreak) {
        updates.timeRemaining = expectedDuration;
      }
      
      // Update break timer if it differs from expected duration
      const expectedBreakDuration = settings.halfTimeBreakDuration * 60;
      if (prev.breakTimeRemaining !== expectedBreakDuration) {
        updates.breakTimeRemaining = expectedBreakDuration;
      }
      
      return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
    });
  }, [settings.halfDuration, settings.overtimeDuration, settings.halfTimeBreakDuration, setGameState]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
    } catch (err) {
      console.error('Failed to save settings:', err);
    }
  }, [settings]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleTimer = () => {
    initAudioContext();
    setGameState(prev => {
      if (!prev.isRunning) {
        try {
          if (prev.isHalfTimeBreak) {
            localStorage.setItem(STORAGE_KEYS.SAVED_TIME, prev.breakTimeRemaining.toString());
          } else {
            localStorage.setItem(STORAGE_KEYS.SAVED_TIME, prev.timeRemaining.toString());
          }
        } catch (err) {
          console.error('Failed to save timer:', err);
        }
        return { ...prev, isRunning: true, startTimestamp: Date.now() };
      } else {
        try {
          localStorage.removeItem(STORAGE_KEYS.SAVED_TIME);
        } catch (err) {
          console.error('Failed to clear saved timer:', err);
        }
        return { ...prev, isRunning: false, startTimestamp: undefined };
      }
    });
  };

  const resetTimer = () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.SAVED_TIME);
    } catch (err) {
      console.error('Failed to clear saved timer:', err);
    }
    setGameState(prev => {
      if (prev.isHalfTimeBreak) {
        return {
          ...prev,
          breakTimeRemaining: settings.halfTimeBreakDuration * 60,
          isRunning: false,
          startTimestamp: undefined
        };
      } else {
        return {
          ...prev,
          timeRemaining: settings.halfDuration * 60,
          isRunning: false,
          startTimestamp: undefined
        };
      }
    });
  };

  const adjustScore = (team: 'home' | 'away', delta: number) => {
    initAudioContext();
    setGameState(prev => ({
      ...prev,
      [team === 'home' ? 'homeScore' : 'awayScore']: 
        Math.max(0, prev[team === 'home' ? 'homeScore' : 'awayScore'] + delta)
    }));
  };

  const addCard = (card: Omit<GameCard, 'id' | 'timestamp' | 'half'>) => {
    initAudioContext();
    const newCard: GameCard = {
      ...card,
      id: Date.now().toString(),
      timestamp: Date.now(),
      half: gameState.currentHalf,
    };

    // Check if this is a second yellow for the same player
    const existingYellow = gameState.cards.find(
      c => c.team === card.team && c.playerNumber === card.playerNumber && c.type === 'yellow'
    );

    if (card.type === 'yellow' && existingYellow) {
      // Remove the existing yellow and issue a red card instead
      const redCard: GameCard = {
        ...card,
        id: Date.now().toString(),
        type: 'red',
        reason: card.reason || 'Second yellow card',
      };
      setGameState(prev => ({
        ...prev,
        cards: [...prev.cards.filter(c => c.id !== existingYellow.id), redCard]
      }));
      toast.success(`🟥 Red card for #${card.playerNumber} (second yellow)`);
    } else {
      setGameState(prev => ({
        ...prev,
        cards: [...prev.cards, newCard]
      }));
      toast.success(`${card.type === 'yellow' ? '🟨' : '🟥'} Card issued to #${card.playerNumber}`);
    }
  };

  const removeCard = (cardId: string) => {
    setCardToRemove(cardId);
  };

  const confirmRemoveCard = () => {
    if (cardToRemove) {
      setGameState(prev => ({
        ...prev,
        cards: prev.cards.filter(c => c.id !== cardToRemove)
      }));
      toast.success('Card removed');
      setCardToRemove(null);
    }
  };

  const confirmNewGame = () => {
    // Save current game to history if it has any meaningful data
    const hasData =
      gameState.homeScore > 0 ||
      gameState.awayScore > 0 ||
      gameState.cards.length > 0 ||
      gameState.notes.trim().length > 0;

    if (hasData) {
      try {
        const historyJson = localStorage.getItem(STORAGE_KEYS.GAME_HISTORY);
        const history = historyJson ? JSON.parse(historyJson) : [];
        const savedGame: SavedGame = {
          id: Date.now().toString(),
          timestamp: Date.now(),
          settings,
          finalScore: {
            home: gameState.homeScore,
            away: gameState.awayScore,
          },
          cards: gameState.cards,
          notes: gameState.notes,
        };
        history.unshift(savedGame);
        localStorage.setItem(STORAGE_KEYS.GAME_HISTORY, JSON.stringify(history));
      } catch (err) {
        console.error('Failed to save game to history:', err);
      }
    }

    // Reset game state
    setGameState({
      homeScore: 0,
      awayScore: 0,
      currentHalf: 1,
      timeRemaining: settings.halfDuration * 60,
      isRunning: false,
      isHalfTimeBreak: false,
      breakTimeRemaining: settings.halfTimeBreakDuration * 60,
      cards: [],
      notes: '',
    });

    // Reset team names to defaults
    setSettings(prev => ({
      ...prev,
      homeTeamName: 'Home',
      awayTeamName: 'Away',
    }));

    toast.success('New game started');
    setShowResetConfirm(false);
  };

  useWakeLock(gameState.isRunning);
  useGameTimer({ gameState, setGameState, settings });

  const nextHalf = () => {
    // Clear saved timer from localStorage
    try {
      localStorage.removeItem(STORAGE_KEYS.SAVED_TIME);
    } catch (err) {
      console.error('Failed to clear saved timer:', err);
    }
    
    if (gameState.currentHalf < settings.numberOfHalves) {
      setGameState(prev => ({
        ...prev,
        currentHalf: prev.currentHalf + 1,
        timeRemaining: settings.halfDuration * 60,
        isRunning: false,
        isHalfTimeBreak: false,
        breakTimeRemaining: settings.halfTimeBreakDuration * 60,
        startTimestamp: undefined
      }));
      toast.success(`Starting half ${gameState.currentHalf + 1}`);
    } else if (settings.hasOvertime) {
      setGameState(prev => ({
        ...prev,
        currentHalf: prev.currentHalf + 1,
        timeRemaining: settings.overtimeDuration * 60,
        isRunning: false,
        isHalfTimeBreak: false,
        breakTimeRemaining: settings.halfTimeBreakDuration * 60,
        startTimestamp: undefined
      }));
      toast.success('Starting overtime');
    }
  };

  const startBreak = () => {
    initAudioContext();
    try {
      localStorage.removeItem(STORAGE_KEYS.SAVED_TIME);
    } catch (err) {
      console.error('Failed to clear saved timer:', err);
    }
    
    setGameState(prev => ({
      ...prev,
      isHalfTimeBreak: true,
      breakTimeRemaining: settings.halfTimeBreakDuration * 60,
      isRunning: true,
      startTimestamp: Date.now()
    }));
    toast.success('Half-time break started');
  };

  const skipBreak = () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.SAVED_TIME);
    } catch (err) {
      console.error('Failed to clear saved timer:', err);
    }
    
    // Transition to next half
    nextHalf();
  };

  const isFinalHalf = gameState.currentHalf >= settings.numberOfHalves && !settings.hasOvertime;
  const hasMoreHalves = gameState.currentHalf < settings.numberOfHalves || settings.hasOvertime;

  return (
    <div className="min-h-[100dvh] p-4 flex flex-col">
      <div className="max-w-2xl mx-auto w-full flex-1 flex flex-col gap-4">
        {/* Header */}
        <div className="flex justify-between items-center gap-2">
          <h1 className="text-3xl sm:text-4xl font-bold text-on-dark truncate" style={{ fontFamily: 'Teko, "Teko Fallback", sans-serif' }}>
            ⚽ REF ASSISTANT
          </h1>
          <div className="flex gap-2">
            {hasHistory && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => setLocation('/history')}
                className="bg-on-dark-10 border-on-dark-20 text-on-dark hover:bg-on-dark-20"
                aria-label="View game history"
              >
                <Archive className="h-5 w-5" />
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setLocation('/settings')}
              className="bg-on-dark-10 border-on-dark-20 text-on-dark hover:bg-on-dark-20"
              aria-label="Open settings"
            >
              <Settings className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Age Group Badge */}
        <div className="flex justify-center">
          <Badge variant="secondary" className="text-lg px-4 py-1 bg-on-dark-90 text-dark-accent font-semibold">
            {settings.ageGroup}
          </Badge>
        </div>

        {/* Timer */}
        <Card className="p-8 bg-card">
          <div className="text-center space-y-4">
            {gameState.isHalfTimeBreak ? (
              <>
                {/* Break Timer Display */}
                <div className="text-sm font-medium text-muted-foreground">
                  Half-Time Break
                </div>
                <div 
                  className="text-7xl font-bold tabular-nums text-foreground"
                  role="timer"
                  aria-live="polite"
                  aria-atomic="true"
                  aria-label={`Break time remaining: ${formatTime(gameState.breakTimeRemaining)}`}
                >
                  {formatTime(gameState.breakTimeRemaining)}
                </div>
                <div className="flex gap-2 justify-center">
                  <Button
                    size="lg"
                    onClick={toggleTimer}
                    variant={gameState.isRunning ? "destructive" : "default"}
                    aria-label={gameState.isRunning ? 'Pause break timer' : 'Start break timer'}
                  >
                    {gameState.isRunning ? <Pause className="mr-2 h-5 w-5" /> : <Play className="mr-2 h-5 w-5" />}
                    {gameState.isRunning ? 'Pause' : 'Start'}
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    onClick={resetTimer}
                    aria-label="Reset break timer"
                  >
                    <RotateCcw className="h-5 w-5" />
                  </Button>
                </div>
                {gameState.breakTimeRemaining === 0 ? (
                  <Button onClick={nextHalf} className="w-full" size="lg">
                    Start Next Half
                  </Button>
                ) : (
                  <Button onClick={skipBreak} variant="outline" className="w-full" size="lg">
                    Skip Break
                  </Button>
                )}
              </>
            ) : (
              <>
                {/* Game Timer Display */}
                <div className="text-sm font-medium text-muted-foreground">
                  {gameState.currentHalf > settings.numberOfHalves
                    ? 'Overtime'
                    : `Half ${gameState.currentHalf} of ${settings.numberOfHalves}`}
                </div>
                <div 
                  className="text-7xl font-bold tabular-nums text-foreground"
                  role="timer"
                  aria-live="polite"
                  aria-atomic="true"
                  aria-label={`Time remaining: ${formatTime(gameState.timeRemaining)}`}
                >
                  {formatTime(gameState.timeRemaining)}
                </div>
                <div className="flex gap-2 justify-center">
                  <Button
                    size="lg"
                    onClick={toggleTimer}
                    variant={gameState.isRunning ? "destructive" : "default"}
                    aria-label={gameState.isRunning ? 'Pause timer' : 'Start timer'}
                  >
                    {gameState.isRunning ? <Pause className="mr-2 h-5 w-5" /> : <Play className="mr-2 h-5 w-5" />}
                    {gameState.isRunning ? 'Pause' : 'Start'}
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    onClick={resetTimer}
                    aria-label="Reset timer to half duration"
                  >
                    <RotateCcw className="h-5 w-5" />
                  </Button>
                </div>
                {gameState.timeRemaining === 0 && (
                  <>
                    {hasMoreHalves ? (
                      <Button onClick={startBreak} className="w-full" size="lg">
                        Start Break
                      </Button>
                    ) : (
                      <Button onClick={() => setLocation('/history')} className="w-full" size="lg">
                        <Archive className="mr-2 h-5 w-5" />
                        View in History
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </Card>

        {/* Score */}
        <div className="grid grid-cols-2 gap-4">
          <ScoreCard
            team="home"
            teamName={settings.homeTeamName}
            teamColor={settings.homeTeamColor}
            score={gameState.homeScore}
            cards={gameState.cards}
            onAdjustScore={(delta) => adjustScore('home', delta)}
            onIssueCard={() => setShowCardDialog('home')}
            onRemoveCard={removeCard}
          />
          <ScoreCard
            team="away"
            teamName={settings.awayTeamName}
            teamColor={settings.awayTeamColor}
            score={gameState.awayScore}
            cards={gameState.cards}
            onAdjustScore={(delta) => adjustScore('away', delta)}
            onIssueCard={() => setShowCardDialog('away')}
            onRemoveCard={removeCard}
          />
        </div>

        {/* Notes */}
        <Card className="p-6 bg-card">
          <div className="space-y-2">
            <Label htmlFor="game-notes" className="text-sm font-medium">
              Game Notes
            </Label>
            <Textarea
              id="game-notes"
              placeholder="Track warnings, coach names, incidents, or any other relevant information..."
              value={gameState.notes}
              onChange={(e) => setGameState(prev => ({ ...prev, notes: e.target.value }))}
              className="min-h-[120px] resize-none"
              aria-label="Game notes"
            />
            <p className="text-xs text-muted-foreground">
              Your notes are automatically saved
            </p>
          </div>
        </Card>

        {/* New Game */}
        <Button
          variant="outline"
          onClick={() => setShowResetConfirm(true)}
          className="w-full"
        >
          New Game
        </Button>
      </div>

      {showCardDialog && (
        <CardDialog
          team={showCardDialog}
          onClose={() => setShowCardDialog(null)}
          onSubmit={addCard}
        />
      )}

      <ConfirmDialog
        open={showResetConfirm}
        onOpenChange={setShowResetConfirm}
        onConfirm={confirmNewGame}
        title="Start New Game?"
        description="The current game will be saved to your history. Scores, cards, and notes will be cleared. Team names will be reset to 'Home' and 'Away'."
        confirmText="New Game"
        cancelText="Keep Game"
      />

      <ConfirmDialog
        open={cardToRemove !== null}
        onOpenChange={(open) => { if (!open) setCardToRemove(null); }}
        onConfirm={confirmRemoveCard}
        title="Remove Card?"
        description="This will remove the card from the game record."
        confirmText="Remove"
        cancelText="Cancel"
      />
    </div>
  );
}

export default GameView;